
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
// import { downloadUrl } from "@/utils/util";
import {
  JieLongCardInfo,
  JieLongStatus,
  JIELONG_STATUS as JIELONG_STATUS_MAP,
  isTerminalJieLongStatus,
  endJielong,
  cancelJielong,
  updateJoinExtendTime,
  parseJielongTime,
  pickJielongEndCountdownTime,
} from "@/api/jielong";
import TipsPopup from "@/components/tipsPopup/index.vue";
import ShareMessageToWeixin from "@/components/ShareMessageToWeixin/index.vue";

interface ActionItem {
  name: string;
  key: string;
  color?: string;
}

@Component({ components: { TipsPopup, ShareMessageToWeixin } })
export default class JieLongCard extends Vue {
  @Prop({ type: Object, required: true }) info!: JieLongCardInfo;
  @Prop({ type: String, default: "/newJielong" }) editRoute!: string;
  @Prop({ type: String, default: "/jielongOrder" }) orderRoute!: string;
  @Prop({ type: String, default: "/jielongPreview" }) detailRoute!: string;
  @Prop({ type: Boolean, default: false }) copyMode!: boolean;

  @Getter logoUrl!: string;
  @Getter companyId!: string;

  JIELONG_STATUS = JIELONG_STATUS_MAP;

  // downloadUrl = downloadUrl;
  sftpUrl = "";

  mounted () {
    this.sftpUrl = this.$store.state.user.sftpUrl
  }

  showActionSheet = false;
  private countdownEnded = false;
  /** 挂载时倒计时是否仍有剩余；已到期则不 emit，避免列表刷新重建后再次 finish 死循环 */
  private hadPositiveCountdown = false;
  /** 关闭弹层后短暂屏蔽卡片点击，避免遮罩点击穿透 */
  private ignoreCardClickUntil = 0;

  // 二次确认弹窗内容
  confirmTitle = "";
  confirmContent = "";
  confirmOkText = "";
  private pendingAction: "end" | "cancel" | null = null;
  private visibilitySaving = false;

  // 终态（已结束 / 已取消）：不弹操作菜单；仅已结束展示「订单管理」按钮
  get isTerminal (): boolean {
    return isTerminalJieLongStatus(this.info.status);
  }

  get isHidden (): boolean {
    return Number(this.info.isHidden) === 1;
  }

  get opBtnText (): string {
    return this.info.status === JIELONG_STATUS_MAP.ENDED ? "订单管理" : "操作";
  }

  // 弹窗操作项：未开始=取消/修改；接龙中=提前结束/修改/订单管理
  get actions (): ActionItem[] {
    if (this.info.status === JIELONG_STATUS_MAP.NOT_STARTED) {
      return [
        { name: "取消接龙", key: "cancel", color: "#f53f3f" },
        { name: "修改接龙", key: "edit" },
      ];
    }
    return [
      { name: "提前结束", key: "end", color: "#f53f3f" },
      { name: "修改接龙", key: "edit" },
      { name: "订单管理", key: "order" },
    ];
  }

  get displayImages (): string[] {
    return (this.info.goodsList || [])
      .map((g) => g.ingredientImageUrl || "")
      .slice(0, 3);
  }

  get priceText (): string {
    const first = (this.info.goodsList || [])[0];
    if (!first) return "";
    const { minPrice, maxPrice } = first;
    if (minPrice == null) return "";
    if (maxPrice == null || maxPrice === minPrice) {
      return `${this.formatMoney(minPrice)}`;
    }
    return `${this.formatMoney(minPrice)}-${this.formatMoney(maxPrice)}`;
  }

  get statusText (): string {
    const map: Record<JieLongStatus, string> = {
      0: "未开始",
      1: "接龙中",
      2: "已结束",
      [-1]: "已取消",
    };
    return map[this.info.status] || "";
  }

  get statusClass (): string {
    if (this.info.status === JIELONG_STATUS_MAP.CANCELLED) {
      return "jlc-status--cancelled";
    }
    if (this.info.status === JIELONG_STATUS_MAP.ENDED) {
      return "jlc-status--ended";
    }
    if (this.info.status === JIELONG_STATUS_MAP.NOT_STARTED) {
      return "jlc-status--notStarted";
    }
    return "jlc-status--ongoing";
  }

  get remainMs (): number {
    const target = pickJielongEndCountdownTime(
      this.info.status,
      this.info.endTime,
      this.info.extendTime
    );
    const diff = parseJielongTime(target) - Date.now();
    return diff > 0 ? diff : 0;
  }

  get remainStartMs (): number {
    const diff = parseJielongTime(this.info.startTime) - Date.now();
    return diff > 0 ? diff : 0;
  }

  get endCountdownKey (): string {
    return `${this.info.endTime}|${this.info.extendTime || ""}`;
  }

  /** 超过 24 小时展示天数，否则仅 HH:mm:ss */
  get startCountdownFormat (): string {
    return this.remainStartMs >= 24 * 60 * 60 * 1000 ? "DD天HH:mm:ss" : "HH:mm:ss";
  }

  get endCountdownFormat (): string {
    return this.remainMs >= 24 * 60 * 60 * 1000 ? "DD天HH:mm:ss" : "HH:mm:ss";
  }

  formatMoney (val: number): string {
    return (Number(val) || 0).toFixed(2);
  }

  @Watch("info.status")
  @Watch("info.extendTime")
  onCountdownSourceChange (): void {
    this.countdownEnded = false;
    this.syncCountdownGuard();
  }

  created (): void {
    this.syncCountdownGuard();
  }

  private syncCountdownGuard (): void {
    if (this.info.status === JIELONG_STATUS_MAP.NOT_STARTED) {
      this.hadPositiveCountdown = this.remainStartMs > 0;
    } else if (this.info.status === JIELONG_STATUS_MAP.ONGOING) {
      this.hadPositiveCountdown = this.remainMs > 0;
    } else {
      this.hadPositiveCountdown = false;
    }
  }

  onCountdownEnd (): void {
    if (this.countdownEnded) return;
    this.countdownEnded = true;
    // 进入页面时已到期：van-count-down 会立刻 finish，不应触发列表刷新
    if (!this.hadPositiveCountdown) return;
    this.$emit("countdown-end", this.info.joinActivityId);
  }

  onCardClick (): void {
    if (this.copyMode) return;
    // 遮罩关闭瞬间的点击会穿透到卡片，短暂忽略
    if (Date.now() < this.ignoreCardClickUntil) return;
    this.$router.push({
      path: this.detailRoute,
      query: { id: this.info.joinActivityId },
    });
  }

  onActionSheetClose (): void {
    this.ignoreCardClickUntil = Date.now() + 500;
  }

  onCopy (): void {
    this.$router.push({
      path: this.editRoute,
      query: { id: this.info.joinActivityId, type: "create" },
    });
  }

  onShare (): void {
    const images = this.displayImages.filter(Boolean)
      .map((img) => this.sftpUrl + img)
      .filter(Boolean);
    (this.$refs.ShareMessageToWeixinRef as any).showPopup({
      type: "activity",
      joinActivityId: this.info.joinActivityId,
      title: this.info.title,
      createTime: this.info.createTime,
      images,
      price: this.priceText,
      storeName: this.$store.state.user.info.storeName || "",
      storeLogo: this.logoUrl,
    });
    this.$emit("share", this.info);
  }

  onOpClick (): void {
    if (this.isTerminal) return;
    this.showActionSheet = true;
  }

  onToggleVisible (): void {
    if (!this.isTerminal || this.visibilitySaving) return;
    const next = this.isHidden ? 0 : 1;
    this.visibilitySaving = true;
    updateJoinExtendTime({
      joinActivityId: this.info.joinActivityId,
      isHidden: next,
    })
      .then(() => {
        this.$set(this.info, "isHidden", next);
        this.$toast(next === 1 ? "已隐藏" : "已显示");
        this.$emit("refresh", this.info.joinActivityId);
      })
      .catch(() => {
        this.$toast("操作失败，请重试");
      })
      .finally(() => {
        this.visibilitySaving = false;
      });
  }

  onOrderClick (): void {
    this.$router.push({
      path: this.orderRoute,
      query: { id: this.info.joinActivityId },
    });
  }

  onActionSelect (action: ActionItem): void {
    if (action.key === "edit") {
      this.$router.push({ path: this.editRoute, query: { id: this.info.joinActivityId } });
      return;
    }
    if (action.key === "order") {
      this.onOrderClick();
      return;
    }
    if (action.key === "end") {
      this.pendingAction = "end";
      this.confirmTitle = "提示";
      this.confirmContent = "确认提前结束该接龙？";
      this.confirmOkText = "确认";
      (this.$refs.confirmPopup as any).showPopup();
      return;
    }
    if (action.key === "cancel") {
      this.pendingAction = "cancel";
      this.confirmTitle = "提示";
      this.confirmContent = "确认取消该接龙？";
      this.confirmOkText = "确认";
      (this.$refs.confirmPopup as any).showPopup();
    }
  }

  onConfirmAction (): void {
    if (this.pendingAction === "end") {
      endJielong({ joinActivityList: [{ joinActivityId: this.info.joinActivityId }], storeId: this.companyId })
        .then(() => {
          this.$toast("已结束接龙");
          this.$emit("refresh", this.info.joinActivityId);
        })
        .catch(() => {
          this.$toast("结束失败，请重试");
        });
    } else if (this.pendingAction === "cancel") {
      cancelJielong({ joinActivityList: [{ joinActivityId: this.info.joinActivityId }], storeId: this.companyId })
        .then(() => {
          this.$toast("已取消接龙");
          this.$emit("refresh", this.info.joinActivityId);
        })
        .catch(() => {
          this.$toast("取消失败，请重试");
        });
    }
    this.pendingAction = null;
  }
}
